import React from 'react';
import { Helmet } from 'react-helmet';
import { ContentReference } from '@/@types/cms';

export interface SEOProps {
  browserTitle?: string;
  navigationTitle?: string;
  name?: string;
  displayName?: string;
  url?: string;
  authorMetaData?: string;
  metaContentType?: string;
  pageDescription?: string;
  keywords?: string[] | string;
  openGraphImage?: ContentReference;
  openGraphTitle?: string;
  twitterCard?: string;
  twitterDescription?: string;
  twitterImage?: ContentReference;
  twitterSite?: string;
  twitterTitle?: string;
  robotsContent?: string;
  openGraphDescription?: string;
  openGraphSiteName?: string;
  allowsExternalSearch?: boolean;
  isExcludedFromSearch?: boolean;
}

const Seo: React.FC<SEOProps> = ({
  browserTitle,
  navigationTitle,
  displayName,
  name,
  url,
  authorMetaData,
  metaContentType,
  pageDescription,
  keywords,
  openGraphImage,
  openGraphTitle,
  twitterDescription,
  twitterImage,
  twitterSite,
  twitterTitle,
  robotsContent,
  openGraphDescription,
  openGraphSiteName,
  allowsExternalSearch,
  isExcludedFromSearch
}) => {
  const robotsValue =
    !isExcludedFromSearch || (isExcludedFromSearch && allowsExternalSearch)
      ? (robotsContent ?? import.meta.env.VITE_ROBOTS_DEFAULT)
      : 'noindex';

  return (
    <Helmet>
      <title>{browserTitle || navigationTitle || displayName || name}</title>
      <meta
        name="title"
        content={browserTitle || navigationTitle || displayName || name}
        key="title"
      ></meta>
      <meta name="robots" content={robotsValue} key="robots"></meta>
      {url && <link rel="canonical" href={url} key="canonical" />}
      {pageDescription && <meta name="description" content={pageDescription} key="description" />}
      {keywords && (
        <meta
          name="keywords"
          content={Array.isArray(keywords) ? keywords.join(', ') : keywords}
          key="keywords"
        />
      )}
      {authorMetaData && <meta name="author" content={authorMetaData} key="author" />}
      {metaContentType && <meta name="content-type" content={metaContentType} key="content-type" />}
      {(openGraphTitle || navigationTitle) && (
        <meta property="og:title" content={openGraphTitle || navigationTitle} key="og:title" />
      )}
      {(openGraphDescription || pageDescription) && (
        <meta
          property="og:description"
          content={openGraphDescription || pageDescription}
          key="og:description"
        />
      )}
      {url && <meta property="og:url" content={url} key="og:url" />}
      {openGraphImage && (
        <meta
          property="og:image"
          content={`${window.location.origin}${openGraphImage.url}`}
          key="og:image"
        />
      )}
      <meta name="twitter:card" content={'summary_large_image'} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} key="twitter:site" />}
      {(twitterTitle || navigationTitle) && (
        <meta name="twitter:title" content={twitterTitle || navigationTitle} key="twitter:title" />
      )}
      {(twitterDescription || pageDescription) && (
        <meta
          name="twitter:description"
          content={twitterDescription || pageDescription}
          key="twitter:description"
        />
      )}
      {twitterImage && (
        <meta
          name="twitter:image"
          content={`${window.location.origin}${twitterImage.url}`}
          key="twitter:image"
        />
      )}
      {twitterImage && (
        <meta
          name="twitter:image:alt"
          content={twitterImage?.altText as string}
          key="twitter:image:alt"
        />
      )}
      <meta property="og:type" content={'website'} key="og:type" />
      <meta property="og:site_name" content={openGraphSiteName || 'Practising Law Institute'} key="og:site_name" />
    </Helmet>
  );
};

export default Seo;
